// Lightweight, self-contained toast stack for the footprint experience.
// Kept separate from useGlobalToast because footprint marks need an inline "Undo"
// affordance: every decision a reader makes about a fragment is reversible.
export interface FootprintToast {
    id: number;
    text: string;
    onUndo?: () => void;
    /** The footprint value this toast's Undo acts on, when it acts on one. */
    valueId?: string;
}

const toasts = ref<FootprintToast[]>([]);
let uid = 0;

export const useFootprintToasts = () => {
    function dismiss(id: number) {
        toasts.value = toasts.value.filter((t) => t.id !== id);
    }

    function push(text: string, onUndo?: () => void, valueId?: string) {
        // One live Undo at a time. A toast carrying an action is never taken away on a
        // timer — five seconds is not enough to tab across the field to reach it, so the
        // control would expire before anyone driving by keyboard or screen reader could
        // press it — which means it has to be superseded instead, or a sorting run buries
        // the page under a column of them. Every older decision keeps its own route back:
        // each row keeps its choice controls available throughout the scan.
        //
        // Superseding by value on top of that is what stops a stale Undo restoring a
        // verdict from two decisions ago over the person's newest choice.
        if (onUndo) {
            toasts.value = toasts.value.filter((t) => !t.onUndo);
        }
        if (valueId !== undefined) {
            toasts.value = toasts.value.filter((t) => t.valueId !== valueId);
        }

        const id = ++uid;
        toasts.value = [...toasts.value, { id, text, onUndo, valueId }];
        if (import.meta.client && !onUndo) {
            setTimeout(() => dismiss(id), 3200);
        }
        return id;
    }

    function undo(toast: FootprintToast) {
        toast.onUndo?.();
        dismiss(toast.id);
    }

    /** The module-level stack must not carry values or Undo actions into another scan. */
    function clear() {
        toasts.value = [];
    }

    return { toasts, push, undo, dismiss, clear };
};
